import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Container from "../components/Container";
import usePortfolioStore from "../store/store";
import DriveImage from "../components/DriveImage";
import Loading from "../components/loading";
import { useTranslation } from "react-i18next";

export default function WorkExperiences() {
  const { t } = useTranslation("common");
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);

  const workExperiences = usePortfolioStore((s) => s.workExperiences || []);
  const fetchWorkRemote = usePortfolioStore((s) => s.fetchWorkRemote);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchWorkRemote();
      setLoading(false);
    };
    loadData();
  }, [fetchWorkRemote]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Container>
      <div className="py-8">
        {/* Header */}
        <div className="mb-4 lg:mb-8">
          <h2
            className={`text-lg md:text-xl md:text-2xl font-bold ${colors.text.primary}`}
          >
            {t("work.title")}
          </h2>
          <p
            className={`text-[11px] sm:text-xs md:text-sm ${colors.text.secondary} mt-1`}
          >
            {t("work.description")}
          </p>
        </div>

        {/* Work Timeline */}
        {workExperiences.length === 0 ? (
          <div
            className={`${colors.background.secondary} rounded-lg p-12 text-center`}
          >
            <svg
              className={`mx-auto h-16 w-16 ${colors.text.muted} mb-4`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <p className={colors.text.secondary}>No work experience found.</p>
          </div>
        ) : (
          <div className="space-y-2 lg:space-y-4">
            {workExperiences.map((work) => (
              <Link
                key={work.id}
                to={`/work-experiences/${work.id}`}
                className={`${colors.background.primary} border ${colors.border} rounded-lg p-3 md:p-5 hover:shadow-md transition-shadow block`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  {/* Company Logo */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-green-400 to-teal-600">
                    <DriveImage
                      src={work.workImageUrl}
                      alt={work.company}
                      className="w-full h-full object-cover"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-white opacity-50"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      }
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3
                      className={`text-sm sm:text-base md:text-lg font-semibold ${colors.text.primary}`}
                    >
                      {work.position}
                    </h3>
                    <p
                      className={`text-[11px] sm:text-xs md:text-sm ${colors.text.secondary} mt-1`}
                    >
                      {work.company}
                    </p>
                  </div>
                  <p
                    className={`text-xs sm:text-sm ${colors.text.muted} whitespace-nowrap`}
                  >
                    {work.startDate && new Date(work.startDate).getFullYear()} -{" "}
                    {work.endDate
                      ? new Date(work.endDate).getFullYear()
                      : t("work.present")}
                  </p>
                </div>
                {work.description && (
                  <p
                    className={`text-[11px] sm:text-xs md:text-sm ${colors.text.secondary} mt-3 line-clamp-2`}
                  >
                    {work.description}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
}
